import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, Zap, MessagesSquare, Mail } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [subject, setSubject] = useState("");
  const [priority, setPriority] = useState("normal");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadContacts = async () => {
    const { data } = await api.get("/messages/contacts");
    setContacts(data);
    setActive((cur) => cur || (data[0]?.id ?? null));
  };

  const loadThread = async (cid) => {
    if (!cid) return;
    const { data } = await api.get("/messages", { params: { with_id: cid } });
    setThread(data);
    data.filter((m) => m.to_id === user.id && !m.read).forEach((m) => api.post(`/messages/${m.id}/read`).catch(() => {}));
  };

  useEffect(() => { loadContacts(); }, []);
  useEffect(() => {
    loadThread(active);
    const id = setInterval(() => loadThread(active), 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!active) return toast.error("Pick someone to message first.");
    if (!text.trim()) return toast.error("Write a message first.");
    setSending(true);
    try {
      await api.post("/messages", { to_id: active, subject, body: text, priority });
      setText(""); setSubject("");
      toast.success(priority === "urgent" ? "Instant message sent" : "Message sent");
      loadThread(active);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  const activeContact = contacts.find((c) => c.id === active);

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <div className="chip mb-2">Message board</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Messages</h1>
        <p className="text-slate-600 mt-1">Send an <b>Instant</b> message for something important, or a <b>Note</b> for questions that can wait.</p>
      </div>

      {contacts.length === 0 ? (
        <div className="p-8 rounded-3xl bg-white border border-slate-200 text-center text-slate-500" data-testid="messages-empty">
          {user.role === "parent" ? "Link a student to start messaging." : "Your parent-teacher isn't linked yet."}
        </div>
      ) : (
        <div className="grid md:grid-cols-[220px_1fr] gap-4">
          {/* Contacts */}
          <div className="space-y-2" data-testid="messages-contacts">
            {contacts.map((c) => (
              <button key={c.id} data-testid={`contact-${c.id}`} onClick={() => setActive(c.id)}
                className={`w-full text-left p-4 rounded-2xl border transition-colors ${active === c.id ? "bg-teal-50 border-teal-300" : "bg-white border-slate-200 hover:border-teal-200"}`}>
                <div className="font-semibold text-slate-900">{c.name}</div>
                <div className="text-xs text-slate-500 capitalize">{c.role}</div>
              </button>
            ))}
          </div>

          {/* Thread + compose */}
          <div className="rounded-3xl bg-white border border-slate-200 flex flex-col min-h-[420px]">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
              <MessagesSquare size={16} className="text-teal-700" />
              <span className="font-semibold text-slate-900">{activeContact?.name || "Conversation"}</span>
            </div>

            <div className="flex-1 p-5 space-y-3 overflow-y-auto max-h-[50vh]" data-testid="messages-thread">
              {thread.length === 0 && <div className="text-sm text-slate-400 text-center mt-8">No messages yet — say hi!</div>}
              {thread.map((m) => {
                const mine = m.from_id === user.id;
                return (
                  <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${mine ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                      {m.priority === "urgent" && (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide mb-1 ${mine ? "text-amber-200" : "text-amber-600"}`}>
                          <Zap size={10} /> Instant
                        </span>
                      )}
                      {m.subject && <div className={`font-semibold ${mine ? "text-white" : "text-slate-900"}`}>{m.subject}</div>}
                      <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                      <div className={`text-[10px] mt-1 ${mine ? "text-teal-100" : "text-slate-400"}`}>
                        {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>

            <form onSubmit={send} className="p-4 border-t border-slate-100 space-y-2">
              <div className="flex gap-2">
                <button type="button" data-testid="msg-priority-normal" onClick={() => setPriority("normal")}
                  className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-semibold ${priority === "normal" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                  <Mail size={12} /> Note
                </button>
                <button type="button" data-testid="msg-priority-urgent" onClick={() => setPriority("urgent")}
                  className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-semibold ${priority === "urgent" ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600"}`}>
                  <Zap size={12} /> Instant
                </button>
              </div>
              <input data-testid="msg-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (optional)"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              <div className="flex gap-2">
                <textarea data-testid="msg-body" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Type your message…"
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                <button data-testid="msg-send" disabled={sending}
                  className="px-5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold flex items-center gap-2 disabled:opacity-60">
                  <Send size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
